import type { TrainingPlan, UserProfile } from '@shared/types';
import { logger } from '../utils/logger';
import apiClient from './api';

/**
 * Training Plan Storage Service
 *
 * Manages training plan persistence with central backend storage and localStorage cache.
 * Uses optimistic updates: saves to localStorage immediately, syncs to backend asynchronously.
 */

const STORAGE_KEYS = {
  TRAINING_PLAN: 'pacepilot_training_plan',
  PLAN_METADATA: 'pacepilot_plan_metadata',
} as const;

/**
 * Intervals.icu sync result from backend auto-sync
 */
export interface IntervalsSyncResult {
  synced: boolean;
  successfulSyncs?: number;
  failedSyncs?: number;
  message?: string;
}

/**
 * Dispatch a custom event with Intervals.icu sync result for UI notification
 */
function dispatchIntervalsSyncEvent(syncResult: IntervalsSyncResult): void {
  if (syncResult.synced) {
    logger.info('Intervals.icu auto-sync completed', {
      successfulSyncs: syncResult.successfulSyncs,
      failedSyncs: syncResult.failedSyncs,
    });
  } else if (syncResult.message) {
    logger.info('Intervals.icu sync skipped', { message: syncResult.message });
  }

  window.dispatchEvent(new CustomEvent('intervals-sync-completed', { detail: syncResult }));
}

interface TrainingPlanMetadata {
  createdAt: string;
  updatedAt: string;
  userProfileHash: string;
  version: string;
}

/**
 * Generate a simple hash of user profile for comparison
 */
function generateUserProfileHash(profile: UserProfile): string {
  const hashData = {
    thresholdPaceSec: profile.thresholdPaceSec,
    weeklyMileage: profile.weeklyMileage,
    weeklyWorkoutPreferences: profile.weeklyWorkoutPreferences,
    fatigueLevel: profile.fatigueLevel,
    // Include advanced settings in hash if they exist
    trainingConstants: profile.trainingConstants,
    fatigueThresholds: profile.fatigueThresholds,
  };

  return btoa(JSON.stringify(hashData));
}

/**
 * Check if localStorage is available
 */
function isStorageAvailable(): boolean {
  try {
    const test = '__storage_test__';
    localStorage.setItem(test, test);
    localStorage.removeItem(test);
    return true;
  } catch {
    return false;
  }
}

/**
 * Save training plan to localStorage and sync to backend
 * Uses optimistic updates: saves locally first, then syncs to backend
 */
export async function saveTrainingPlan(
  trainingPlan: TrainingPlan,
  userProfile: UserProfile
): Promise<boolean> {
  if (!isStorageAvailable()) {
    logger.warn('localStorage not available, cannot save training plan');
    return false;
  }

  try {
    const metadata: TrainingPlanMetadata = {
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      userProfileHash: generateUserProfileHash(userProfile),
      version: '1.0.0',
    };

    // Save to localStorage first (optimistic update)
    localStorage.setItem(STORAGE_KEYS.TRAINING_PLAN, JSON.stringify(trainingPlan));
    localStorage.setItem(STORAGE_KEYS.PLAN_METADATA, JSON.stringify(metadata));

    logger.info('Training plan saved to localStorage');

    // Sync to backend asynchronously (backend also auto-syncs to Intervals.icu)
    try {
      const response = await apiClient.updateStoredTrainingPlan(trainingPlan);
      if (response.success) {
        logger.info('Training plan synced to backend');

        // Dispatch Intervals.icu sync result if available
        if (response.data?.intervalsSync) {
          dispatchIntervalsSyncEvent(response.data.intervalsSync);
        }
      } else {
        logger.warn('Failed to sync training plan to backend', {
          message: response.message,
        });
      }
    } catch (error) {
      logger.warn('Could not sync training plan to backend', { error });
      // Don't fail the operation - localStorage save succeeded
    }

    return true;
  } catch (error) {
    logger.error('Failed to save training plan to localStorage', { error });
    return false;
  }
}

/**
 * Load training plan from backend (with localStorage fallback)
 * Priority: Backend → localStorage cache
 *
 * @param userProfile - Optional user profile to update metadata hash when caching
 *                      Pass this to ensure compatibility checks pass after loading from backend
 */
export async function loadTrainingPlan(userProfile?: UserProfile): Promise<TrainingPlan | null> {
  // Try loading from backend first
  try {
    logger.info('Loading training plan from backend...');
    const response = await apiClient.getStoredTrainingPlan();

    if (response.success && response.data) {
      logger.info('Training plan loaded from backend');

      // Update localStorage cache with proper metadata
      if (isStorageAvailable()) {
        try {
          localStorage.setItem(STORAGE_KEYS.TRAINING_PLAN, JSON.stringify(response.data));

          // If userProfile is provided, update metadata to ensure compatibility check passes
          if (userProfile) {
            const metadata: TrainingPlanMetadata = {
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
              userProfileHash: generateUserProfileHash(userProfile),
              version: '1.0.0',
            };
            localStorage.setItem(STORAGE_KEYS.PLAN_METADATA, JSON.stringify(metadata));
            logger.info('Training plan cached to localStorage with metadata');
          } else {
            logger.info('Training plan cached to localStorage');
          }
        } catch (error) {
          logger.warn('Could not cache training plan to localStorage', { error });
        }
      }

      return response.data;
    }
  } catch (error) {
    logger.warn('Could not load training plan from backend, trying localStorage', { error });
  }

  // Fallback to localStorage
  if (!isStorageAvailable()) {
    return null;
  }

  try {
    const planData = localStorage.getItem(STORAGE_KEYS.TRAINING_PLAN);
    if (!planData) {
      logger.info('No training plan found in localStorage');
      return null;
    }

    const trainingPlan: TrainingPlan = JSON.parse(planData);

    // Basic validation - check for weeks array (new structure)
    if (!trainingPlan.weeks || !Array.isArray(trainingPlan.weeks)) {
      logger.warn('Invalid training plan structure in localStorage');
      clearTrainingPlan();
      return null;
    }

    logger.info('Training plan loaded from localStorage cache');
    return trainingPlan;
  } catch (error) {
    logger.error('Failed to load training plan from localStorage', error);
    clearTrainingPlan();
    return null;
  }
}

/**
 * Load training plan synchronously from localStorage only (no backend fetch)
 * Use this for immediate access when you need the cached plan without waiting
 */
export function loadTrainingPlanSync(): TrainingPlan | null {
  if (!isStorageAvailable()) {
    return null;
  }

  try {
    const planData = localStorage.getItem(STORAGE_KEYS.TRAINING_PLAN);
    if (!planData) {
      return null;
    }

    const trainingPlan: TrainingPlan = JSON.parse(planData);

    // Basic validation - check for weeks array (new structure)
    if (!trainingPlan.weeks || !Array.isArray(trainingPlan.weeks)) {
      logger.warn('Invalid training plan structure in localStorage');
      clearTrainingPlan();
      return null;
    }

    return trainingPlan;
  } catch (error) {
    logger.error('Failed to load training plan from localStorage', error);
    clearTrainingPlan();
    return null;
  }
}

/**
 * Load training plan metadata
 */
export function loadTrainingPlanMetadata(): TrainingPlanMetadata | null {
  if (!isStorageAvailable()) {
    return null;
  }

  try {
    const metadataData = localStorage.getItem(STORAGE_KEYS.PLAN_METADATA);
    if (!metadataData) {
      return null;
    }

    return JSON.parse(metadataData);
  } catch (error) {
    logger.error('Failed to load training plan metadata', error);
    return null;
  }
}

/**
 * Check if stored plan is compatible with current user profile
 */
export function isStoredPlanCompatible(userProfile: UserProfile): boolean {
  const metadata = loadTrainingPlanMetadata();
  if (!metadata) {
    return false;
  }

  const currentHash = generateUserProfileHash(userProfile);
  return metadata.userProfileHash === currentHash;
}

/**
 * Update the metadata hash for the currently cached plan
 * Call this after loading a plan from backend to ensure compatibility checks pass
 */
export function updateCachedPlanMetadata(userProfile: UserProfile): boolean {
  if (!isStorageAvailable()) {
    return false;
  }

  // Only update if there's actually a plan cached
  const planData = localStorage.getItem(STORAGE_KEYS.TRAINING_PLAN);
  if (!planData) {
    return false;
  }

  try {
    const existingMetadata = loadTrainingPlanMetadata();
    const metadata: TrainingPlanMetadata = {
      createdAt: existingMetadata?.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      userProfileHash: generateUserProfileHash(userProfile),
      version: '1.0.0',
    };

    localStorage.setItem(STORAGE_KEYS.PLAN_METADATA, JSON.stringify(metadata));
    logger.info('Updated cached plan metadata hash');
    return true;
  } catch (error) {
    logger.error('Failed to update cached plan metadata', error);
    return false;
  }
}

/**
 * Update stored training plan with new data and sync to backend
 * Uses optimistic updates: saves locally first, then syncs to backend
 */
export async function updateTrainingPlan(
  trainingPlan: TrainingPlan,
  userProfile: UserProfile
): Promise<boolean> {
  if (!isStorageAvailable()) {
    return false;
  }

  try {
    const existingMetadata = loadTrainingPlanMetadata();
    const metadata: TrainingPlanMetadata = {
      createdAt: existingMetadata?.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      userProfileHash: generateUserProfileHash(userProfile),
      version: '1.0.0',
    };

    // Update localStorage first (optimistic update)
    localStorage.setItem(STORAGE_KEYS.TRAINING_PLAN, JSON.stringify(trainingPlan));
    localStorage.setItem(STORAGE_KEYS.PLAN_METADATA, JSON.stringify(metadata));

    logger.info('Training plan updated in localStorage');

    // Sync to backend asynchronously (backend also auto-syncs to Intervals.icu)
    try {
      const response = await apiClient.updateStoredTrainingPlan(trainingPlan);
      if (response.success) {
        logger.info('Training plan synced to backend');

        // Dispatch Intervals.icu sync result if available
        if (response.data?.intervalsSync) {
          dispatchIntervalsSyncEvent(response.data.intervalsSync);
        }
      } else {
        logger.warn('Failed to sync training plan to backend', { message: response.message });
      }
    } catch (error) {
      logger.warn('Could not sync training plan to backend', { error });
      // Don't fail the operation - localStorage update succeeded
    }

    return true;
  } catch (error) {
    logger.error('Failed to update training plan in localStorage', error);
    return false;
  }
}

/**
 * Clear training plan from localStorage
 */
export function clearTrainingPlan(): void {
  if (!isStorageAvailable()) {
    return;
  }

  try {
    localStorage.removeItem(STORAGE_KEYS.TRAINING_PLAN);
    localStorage.removeItem(STORAGE_KEYS.PLAN_METADATA);
    logger.info('Training plan cleared from localStorage');
  } catch (error) {
    logger.error('Failed to clear training plan from localStorage', error);
  }
}

/**
 * Get storage info for debugging
 */
export function getStorageInfo(): {
  hasStoredPlan: boolean;
  planSize?: number;
  metadata?: TrainingPlanMetadata;
  storageAvailable: boolean;
} {
  const storageAvailable = isStorageAvailable();

  if (!storageAvailable) {
    return { hasStoredPlan: false, storageAvailable: false };
  }

  const planData = localStorage.getItem(STORAGE_KEYS.TRAINING_PLAN);
  const metadata = loadTrainingPlanMetadata();

  return {
    hasStoredPlan: !!planData,
    planSize: planData ? planData.length : undefined,
    metadata: metadata || undefined,
    storageAvailable: true,
  };
}
